import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory, useLocation } from 'react-router-dom';

import { bucketS3, caminhoDocumentarioSennaBeco } from 'constants/EnvironmentData';
import Loading from 'components/Loading';
import useUserData from 'hooks/useUserData';
import OnError from 'core/onError/OnError';
import { BecoMovieService } from 'core/http/service/BecoMovie.service';
import TipoAcessibilidade from 'models/becoMovie/TipoAcessibilidade';

import Styled from './SennaBeco.styled';
import collage from './assets/collage.png';
import grain from './assets/grain.png';
import iconAudiodescricao from './assets/icon-audiodescricao.svg';
import iconLibras from './assets/icon-libras.svg';
import classificacao from './assets/classificacao.svg';
import closeIcon from '../../images/close.svg';
import lineSeparator from './assets/line-separator.svg';
import logoAlvarezMarsal from './assets/logo-alvarez-marsal.svg';
import logoInstituto from './assets/logo-instituto.svg';
import logoItau from './assets/logo-itau.png';
// import logoMinc from './assets/logo-minc.svg';
import logoMinc from './assets/logo-minc-novo.svg';
import logoRouanet from './assets/logo-rouanet.svg';
import logoSennaWordmark from './assets/logo-senna-wordmark.svg';
import logoShp from './assets/logo-shp.svg';

const VideoBaseUrl = 'https://humane-s3-cdn.institutoayrtonsenna.org.br/';

const VideoFiles: Record<TipoAcessibilidade, string> = {
    [TipoAcessibilidade.ORIGINAL]: 'MASTER_BECO_O_CORACAO_QUE_BATIA_DENTRO_DO_AYRTON_LEGENDACC.mp4',
    [TipoAcessibilidade.LIBRAS]: 'MASTER_05_LIBRAS_FULL_CUT.mp4',
    [TipoAcessibilidade.AUDIODESCRICAO]: 'BECO_O_CORACAO_QUE_BATIA_DENTRO_DO_AYRTON_ROUANET_AUDIODESCRICAO_01.mp4',
};

const isTipoAcessibilidade = (value: unknown): value is TipoAcessibilidade =>
    Object.values(TipoAcessibilidade).includes(value as TipoAcessibilidade);

const SennaBeco = () => {
    const history = useHistory();
    const location = useLocation();
    const { loadData } = useUserData();

    const [showVideoModal, setShowVideoModal] = useState(false);
    const [hasProfile, setHasProfile] = useState(false);
    const [videoVersion, setVideoVersion] = useState<TipoAcessibilidade>(TipoAcessibilidade.ORIGINAL);

    // Version of the user's latest access log; a new log is only sent when the
    // version changes. Undefined until the initial check of the page access.
    const loggedVersion = useRef<TipoAcessibilidade>();

    const logAccess = async (version: TipoAcessibilidade) => {
        loggedVersion.current = version;

        try {
            await new BecoMovieService().logAccess({
                'user-agent': navigator.userAgent,
                'tipo-acessibilidade': version,
            });
        } catch (error) {
            console.error('SennaBeco: failed to log access', error);
        }
    };

    const handleOpenVideo = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        setShowVideoModal(true);
    };

    const handleCloseVideo = () => {
        setShowVideoModal(false);
    };

    // Selecting the active version again goes back to the original video.
    const handleSelectVersion = (version: TipoAcessibilidade) => {
        const next = videoVersion === version ? TipoAcessibilidade.ORIGINAL : version;
        setVideoVersion(next);

        if (loggedVersion.current !== next) {
            logAccess(next);
        }
    };

    const getS3Url = () => {
        return bucketS3() + caminhoDocumentarioSennaBeco();
    };

    // The page requires a registered profile (PerfilUsuario). Users who are logged in
    // but have no profile are sent to the profile registration, which brings them
    // back to this route (postProfilePath) once the register is concluded.
    useEffect(() => {
        const verifyProfile = async () => {
            try {
                const { perfilUsuario } = await loadData();

                if (perfilUsuario && perfilUsuario.codigo !== -1) {
                    setHasProfile(true);
                } else {
                    history.replace({
                        pathname: '/profile',
                        state: { postProfilePath: location.pathname },
                    });
                }
            } catch (error) {
                OnError.sendToAnalytics('SennaBeco.verifyProfile', String(error));
                history.replace('/');
            }
        };

        verifyProfile();
    }, []);

    // Every access is logged for Lei Rouanet accountability (the API reads the
    // logged-in user from the auth token). Runs once per page load, after the
    // profile is verified; failures must never affect the page. Users who
    // already have a log aren't logged again: their last chosen version is
    // loaded instead, and only switching it sends a new log.
    useEffect(() => {
        if (!hasProfile) return;

        const checkAccess = async () => {
            let previousVersion: TipoAcessibilidade | null = null;

            try {
                const response = await new BecoMovieService().getTipoAcessibilidade();
                const tipo = response?.['tipo-acessibilidade'];
                previousVersion = isTipoAcessibilidade(tipo) ? tipo : null;
            } catch (error) {
                console.error('SennaBeco: failed to get the accessibility type', error);
            }

            // The user already switched the version while this was loading.
            if (loggedVersion.current) return;

            if (previousVersion) {
                loggedVersion.current = previousVersion;
                setVideoVersion(previousVersion);
            } else {
                logAccess(TipoAcessibilidade.ORIGINAL);
            }
        };

        checkAccess();
    }, [hasProfile]);

    if (!hasProfile) {
        return <Loading />;
    }

    return (
        <Styled.Page $grainSrc={grain}>
            <Helmet>
                <title>Antes de Ayrton Senna, Beco. Antes do Grande Público, Você. | humane</title>
                <meta
                    name="description"
                    content="Um documentário que conta a história do homem por trás do mito Ayrton Senna."
                    data-react-helmet="true"
                />
                <link href="https://fonts.googleapis.com/css2?family=Big+Shoulders+Display:wght@700&display=swap" rel="stylesheet" />
            </Helmet>

            <Styled.KeyVisual>
                <Styled.FrameSvg viewBox="0 0 1920 1080">
                    <path
                        d="M 1857 380 L 1857 870 Q 1857 910 1817 910 L 113 910 Q 73 910 73 870 L 73 170 Q 73 130 113 130 L 1364 130 Q 1394 130 1394 160 L 1394 300 Q 1394 330 1424 330 L 1682 330"
                        fill="none"
                        stroke="#9d7644"
                        strokeWidth="2.6"
                    />
                    <path
                        d="M 1458 130 L 1827 130 Q 1857 130 1857 160 L 1857 328 Q 1857 299 1819 299 L 1458 299 Q 1428 299 1428 269 L 1428 160 Q 1428 130 1458 130 Z"
                        fill="#9d7644"
                    />
                    <path
                        d="M 1680 330 L 1807 330 Q 1857 330 1857 380 L 1857 530 Q 1857 500 1823 500 L 1680 500 Q 1650 500 1650 470 L 1650 360 Q 1650 330 1680 330 Z"
                        fill="#efefef"
                        stroke="#9d7644"
                        strokeWidth="2.6"
                    />
                    <circle cx="1750" cy="414" r="51" fill="#9d7644" />
                    <path
                        d="M 1738 391 L 1771 412 Q 1775 414 1771 416.5 L 1738 437 Q 1733 440 1733 435 L 1733 393 Q 1733 388 1738 391 Z"
                        fill="#ffffff"
                    />
                </Styled.FrameSvg>

                <Styled.CollageMask>
                    <Styled.Collage src={collage} alt="Colagem de fotografias de Ayrton Senna formando a silhueta de seu perfil" />
                </Styled.CollageMask>

                <Styled.Eyebrow>
                    MINISTÉRIO DA CULTURA APRESENTA:{' '}
                    <Styled.EyebrowHighlight>BECO, O CORAÇÃO QUE BATIA DENTRO DO AYRTON.</Styled.EyebrowHighlight>
                </Styled.Eyebrow>

                <Styled.Headline>
                    <Styled.HeadlineLine>ANTES DE</Styled.HeadlineLine>
                    <Styled.HeadlineLine>AYRTON SENNA,</Styled.HeadlineLine>
                    <Styled.HeadlineHighlight>BECO.</Styled.HeadlineHighlight>
                </Styled.Headline>

                <Styled.CtaText>
                    <Styled.CtaTextLine>ASSISTA E LEVE</Styled.CtaTextLine>
                    <Styled.CtaTextLine>PARA SEUS ALUNOS</Styled.CtaTextLine>
                </Styled.CtaText>

                <Styled.PlayButton href="#" aria-label="Assistir ao trailer" onClick={handleOpenVideo} />

                <Styled.Description>
                    Um documentário que conta a história
                    <br />
                    do homem por trás do mito Ayrton Senna.
                </Styled.Description>

                <Styled.ClassificationBadge src={classificacao} alt="Classificação indicativa: livre para todos os públicos" />

                <Styled.FooterContainer>
                    <Styled.FooterLabelPatrocinio>Patrocínio</Styled.FooterLabelPatrocinio>
                    <Styled.FooterLabelRealizacao>Realização</Styled.FooterLabelRealizacao>
                    <Styled.FooterLogosRow>
                        <Styled.FooterLogoRouanet src={logoRouanet} alt="Lei Rouanet" />
                        <Styled.FooterLogoSeparator src={lineSeparator} alt="" />
                        <Styled.FooterLogoItau src={logoItau} alt="Itaú" />
                        <Styled.FooterLogoSeparator src={lineSeparator} alt="" />
                        <Styled.FooterLogoAlvarezMarsal src={logoAlvarezMarsal} alt="Alvarez & Marsal" />
                        <Styled.FooterLogoSeparator src={lineSeparator} alt="" />
                        <Styled.FooterLogoShp src={logoShp} alt="Sweethearted Pirates Films" />
                        <Styled.FooterLogoSeparator src={lineSeparator} alt="" />
                        <Styled.FooterLogoSenna src={logoSennaWordmark} alt="Senna" />
                        <Styled.FooterLogoSeparator src={lineSeparator} alt="" />
                        <Styled.FooterLogoInstituto src={logoInstituto} alt="Instituto Ayrton Senna" />
                        <Styled.FooterLogoSeparator src={lineSeparator} alt="" />
                        <Styled.FooterLogoMinc src={logoMinc} alt="Ministério da Cultura" />
                    </Styled.FooterLogosRow>
                </Styled.FooterContainer>
            </Styled.KeyVisual>

            <Styled.KeyVisualLaptop>
                <Styled.FrameSvg viewBox="0 0 1225 1018">
                    <path
                        d="M 1185 327 L 1185 790 Q 1185 830 1145 830 L 80 830 Q 40 830 40 790 L 40 190 Q 40 150 80 150 L 850 150 Q 872 150 872 172 L 872 270 Q 872 292 894 292 L 1072 292"
                        fill="none"
                        stroke="#9d7644"
                        strokeWidth="2.6"
                    />
                    <path
                        d="M 922 150 L 1163 150 Q 1185 150 1185 172 L 1185 290 Q 1185 270 1159 270 L 922 270 Q 900 270 900 248 L 900 172 Q 900 150 922 150 Z"
                        fill="#9d7644"
                    />
                    <path
                        d="M 1067 292 L 1150 292 Q 1185 292 1185 327 L 1185 424 Q 1185 402 1159 402 L 1067 402 Q 1045 402 1045 380 L 1045 314 Q 1045 292 1067 292 Z"
                        fill="#efefef"
                        stroke="#9d7644"
                        strokeWidth="2.6"
                    />
                    <circle cx="1115" cy="347" r="36" fill="#9d7644" />
                    <path
                        d="M 1107 331 L 1130 345 Q 1133 347 1130 349 L 1107 363 Q 1103 365.5 1103 362 L 1103 332 Q 1103 328.5 1107 331 Z"
                        fill="#ffffff"
                    />
                </Styled.FrameSvg>

                <Styled.LaptopCollageMask>
                    <Styled.LaptopCollage src={collage} alt="Colagem de fotografias de Ayrton Senna formando a silhueta de seu perfil" />
                </Styled.LaptopCollageMask>

                <Styled.LaptopEyebrow>
                    MINISTÉRIO DA CULTURA APRESENTA:{' '}
                    <Styled.EyebrowHighlight>BECO, O CORAÇÃO QUE BATIA DENTRO DO AYRTON.</Styled.EyebrowHighlight>
                </Styled.LaptopEyebrow>

                <Styled.LaptopHeadlineLine $top="22.3969%">ANTES DE</Styled.LaptopHeadlineLine>
                <Styled.LaptopHeadlineLine $top="28.723%">AYRTON SENNA,</Styled.LaptopHeadlineLine>
                <Styled.LaptopHeadlineHighlight $top="34.371%">BECO.</Styled.LaptopHeadlineHighlight>

                <Styled.LaptopCtaText>
                    <Styled.LaptopCtaTextLine>ASSISTA E LEVE</Styled.LaptopCtaTextLine>
                    <Styled.LaptopCtaTextLine>PARA SEUS ALUNOS</Styled.LaptopCtaTextLine>
                </Styled.LaptopCtaText>

                <Styled.LaptopPlayButton href="#" aria-label="Assistir ao trailer" onClick={handleOpenVideo} />

                <Styled.LaptopDescription>
                    Um documentário que conta a história
                    <br />
                    do homem por trás do mito Ayrton Senna.
                </Styled.LaptopDescription>

                <Styled.LaptopClassificationBadge src={classificacao} alt="Classificação indicativa: livre para todos os públicos" />

                <Styled.LaptopFooterContainer>
                    <Styled.LaptopFooterLabelPatrocinio>Patrocínio</Styled.LaptopFooterLabelPatrocinio>
                    <Styled.LaptopFooterLabelRealizacao>Realização</Styled.LaptopFooterLabelRealizacao>
                    <Styled.LaptopFooterLogosRow>
                        <Styled.LaptopFooterLogoRouanet src={logoRouanet} alt="Lei Rouanet" />
                        <Styled.LaptopFooterLogoSeparator src={lineSeparator} alt="" />
                        <Styled.LaptopFooterLogoItau src={logoItau} alt="Itaú" />
                        <Styled.LaptopFooterLogoSeparator src={lineSeparator} alt="" />
                        <Styled.LaptopFooterLogoAlvarezMarsal src={logoAlvarezMarsal} alt="Alvarez & Marsal" />
                        <Styled.LaptopFooterLogoSeparator src={lineSeparator} alt="" />
                        <Styled.LaptopFooterLogoShp src={logoShp} alt="Sweethearted Pirates Films" />
                        <Styled.LaptopFooterLogoSeparator src={lineSeparator} alt="" />
                        <Styled.LaptopFooterLogoSenna src={logoSennaWordmark} alt="Senna" />
                        <Styled.LaptopFooterLogoSeparator src={lineSeparator} alt="" />
                        <Styled.LaptopFooterLogoInstituto src={logoInstituto} alt="Instituto Ayrton Senna" />
                        <Styled.LaptopFooterLogoSeparator src={lineSeparator} alt="" />
                        <Styled.LaptopFooterLogoMinc src={logoMinc} alt="Ministério da Cultura" />
                    </Styled.LaptopFooterLogosRow>
                </Styled.LaptopFooterContainer>
            </Styled.KeyVisualLaptop>

            <Styled.KeyVisualTablet>
                <Styled.FrameSvg viewBox="0 0 1024 1366">
                    <path
                        d="M 965 290 L 965 977 Q 965 1010 932 1010 L 92 1010 Q 59 1010 59 977 L 59 123 Q 59 90 92 90 L 543 90 Q 576 90 576 123 L 576 227 Q 576 260 609 260 L 815 260"
                        fill="none"
                        stroke="#9d7644"
                        strokeWidth="2.6"
                    />
                    <path
                        d="M 631 88 L 940 88 Q 965 88 965 113 L 965 253 Q 965 229 933 229 L 631 229 Q 606 229 606 204 L 606 113 Q 606 88 631 88 Z"
                        fill="#9d7644"
                    />
                    <path
                        d="M 810 260 L 935 260 Q 965 260 965 290 L 965 449 Q 965 419 935 419 L 810 419 Q 780 419 780 389 L 780 290 Q 780 260 810 260 Z"
                        fill="#efefef"
                        stroke="#9d7644"
                        strokeWidth="2.6"
                    />
                    <circle cx="872.5" cy="339.5" r="43" fill="#9d7644" />
                    <path d="M 861 317 L 892 339 Q 896 341.5 892 344 L 861 366 Q 856 369 856 364 L 856 322 Q 856 314 861 317 Z" fill="#ffffff" />
                </Styled.FrameSvg>

                <Styled.TabletCollage src={collage} alt="Colagem de fotografias de Ayrton Senna formando a silhueta de seu perfil" />

                <Styled.TabletEyebrow>
                    MINISTÉRIO DA CULTURA APRESENTA:{' '}
                    <Styled.EyebrowHighlight>BECO, O CORAÇÃO QUE BATIA DENTRO DO AYRTON.</Styled.EyebrowHighlight>
                </Styled.TabletEyebrow>

                <Styled.TabletHeadline>
                    <Styled.TabletHeadlineLine>ANTES DE</Styled.TabletHeadlineLine>
                    <Styled.TabletHeadlineLine>AYRTON SENNA,</Styled.TabletHeadlineLine>
                    <Styled.TabletHeadlineHighlight>BECO.</Styled.TabletHeadlineHighlight>
                </Styled.TabletHeadline>

                <Styled.TabletCtaText>
                    <Styled.TabletCtaTextLine>ASSISTA E LEVE</Styled.TabletCtaTextLine>
                    <Styled.TabletCtaTextLine>PARA SEUS ALUNOS</Styled.TabletCtaTextLine>
                </Styled.TabletCtaText>

                <Styled.TabletPlayButton href="#" aria-label="Assistir ao trailer" onClick={handleOpenVideo} />

                <Styled.TabletDescription>
                    Um documentário que conta a história
                    <br />
                    do homem por trás do mito Ayrton Senna.
                </Styled.TabletDescription>

                <Styled.TabletClassificationBadge src={classificacao} alt="Classificação indicativa: livre para todos os públicos" />

                <Styled.TabletFooterContainer>
                    <Styled.TabletFooterLabelPatrocinio>Patrocínio</Styled.TabletFooterLabelPatrocinio>
                    <Styled.TabletFooterLabelRealizacao>Realização</Styled.TabletFooterLabelRealizacao>
                    <Styled.TabletFooterLogosRow>
                        <Styled.TabletFooterLogoRouanet src={logoRouanet} alt="Lei Rouanet" />
                        <Styled.TabletFooterLogoSeparator src={lineSeparator} alt="" />
                        <Styled.TabletFooterLogoItau src={logoItau} alt="Itaú" />
                        <Styled.TabletFooterLogoSeparator src={lineSeparator} alt="" />
                        <Styled.TabletFooterLogoAlvarezMarsal src={logoAlvarezMarsal} alt="Alvarez & Marsal" />
                        <Styled.TabletFooterLogoSeparator src={lineSeparator} alt="" />
                        <Styled.TabletFooterLogoShp src={logoShp} alt="Sweethearted Pirates Films" />
                        <Styled.TabletFooterLogoSeparator src={lineSeparator} alt="" />
                        <Styled.TabletFooterLogoSenna src={logoSennaWordmark} alt="Senna" />
                        <Styled.TabletFooterLogoSeparator src={lineSeparator} alt="" />
                        <Styled.TabletFooterLogoInstituto src={logoInstituto} alt="Instituto Ayrton Senna" />
                        <Styled.TabletFooterLogoSeparator src={lineSeparator} alt="" />
                        <Styled.TabletFooterLogoMinc src={logoMinc} alt="Ministério da Cultura" />
                    </Styled.TabletFooterLogosRow>
                </Styled.TabletFooterContainer>
            </Styled.KeyVisualTablet>

            <Styled.KeyVisualMobile>
                <Styled.FrameSvg viewBox="0 0 1080 1740">
                    <path
                        d="M 1026 350 L 1026 1494 Q 1026 1534 986 1534 L 95 1534 Q 55 1534 55 1494 L 55 163 Q 55 123 95 123 L 588 123 Q 622 123 622 157 L 622 276 Q 622 306 652 306 L 873 306"
                        fill="none"
                        stroke="#9d7644"
                        strokeWidth="2.6"
                    />
                    <path
                        d="M 674 131 L 1000 131 Q 1026 131 1026 157 L 1026 311 Q 1026 285 993 285 L 674 285 Q 648 285 648 259 L 648 157 Q 648 131 674 131 Z"
                        fill="#9d7644"
                    />
                    <path
                        d="M 871 306 L 982 306 Q 1026 306 1026 350 L 1026 482 Q 1026 456 1000 456 L 871 456 Q 845 456 845 430 L 845 332 Q 845 306 871 306 Z"
                        fill="#efefef"
                        stroke="#9d7644"
                        strokeWidth="2.6"
                    />
                    <circle cx="933" cy="381" r="44" fill="#9d7644" />
                    <path d="M 922 360 L 951 378 Q 955 381 951 383.5 L 922 401 Q 917 404 917 399 L 917 363 Q 917 357 922 360 Z" fill="#ffffff" />
                </Styled.FrameSvg>

                <Styled.MobileCollage src={collage} alt="Colagem de fotografias de Ayrton Senna formando a silhueta de seu perfil" />

                <Styled.MobileEyebrow>
                    MINISTÉRIO DA CULTURA APRESENTA:{' '}
                    <Styled.EyebrowHighlight>BECO, O CORAÇÃO QUE BATIA DENTRO DO AYRTON.</Styled.EyebrowHighlight>
                </Styled.MobileEyebrow>

                <Styled.MobileHeadline>
                    <Styled.MobileHeadlineLine>ANTES DE</Styled.MobileHeadlineLine>
                    <Styled.MobileHeadlineLine>AYRTON SENNA,</Styled.MobileHeadlineLine>
                    <Styled.MobileHeadlineHighlight>BECO.</Styled.MobileHeadlineHighlight>
                </Styled.MobileHeadline>

                <Styled.MobileCtaText>
                    <Styled.MobileCtaTextLine>ASSISTA E LEVE</Styled.MobileCtaTextLine>
                    <Styled.MobileCtaTextLine>PARA SEUS ALUNOS</Styled.MobileCtaTextLine>
                </Styled.MobileCtaText>

                <Styled.MobilePlayButton href="#" aria-label="Assistir ao trailer" onClick={handleOpenVideo} />

                <Styled.MobileDescription>
                    Um documentário que conta a história do
                    <br />
                    homem por trás do mito Ayrton Senna.
                </Styled.MobileDescription>

                <Styled.MobileClassificationBadge src={classificacao} alt="Classificação indicativa: livre para todos os públicos" />
            </Styled.KeyVisualMobile>

            <Styled.MobileSponsors>
                <Styled.MobileSponsorLogo>
                    <Styled.MobileSponsorLogoRouanet src={logoRouanet} alt="Lei Rouanet" />
                </Styled.MobileSponsorLogo>
                <Styled.MobileSponsorLogo>
                    <Styled.MobileSponsorLogoItau src={logoItau} alt="Itaú" />
                </Styled.MobileSponsorLogo>
                <Styled.MobileSponsorLogo>
                    <Styled.MobileSponsorLogoAlvarezMarsal src={logoAlvarezMarsal} alt="Alvarez & Marsal" />
                </Styled.MobileSponsorLogo>
                <Styled.MobileSponsorLogo>
                    <Styled.MobileSponsorLogoShp src={logoShp} alt="Sweethearted Pirates Films" />
                </Styled.MobileSponsorLogo>
                <Styled.MobileSponsorLogo>
                    <Styled.MobileSponsorLogoSenna src={logoSennaWordmark} alt="Senna" />
                </Styled.MobileSponsorLogo>
                <Styled.MobileSponsorLogo>
                    <Styled.MobileSponsorLogoInstituto src={logoInstituto} alt="Instituto Ayrton Senna" />
                </Styled.MobileSponsorLogo>
                <Styled.MobileSponsorLogo>
                    <Styled.MobileSponsorLogoMinc src={logoMinc} alt="Ministério da Cultura" />
                </Styled.MobileSponsorLogo>
            </Styled.MobileSponsors>

            <Styled.VideoModal show={showVideoModal} onHide={handleCloseVideo} centered>
                <Styled.VideoModalBody>
                    <Styled.VideoModalCloseContainer>
                        <Styled.VideoModalClose src={closeIcon} alt="Fechar vídeo" onClick={handleCloseVideo} />
                    </Styled.VideoModalCloseContainer>
                    <Styled.VideoPlayer
                        controls
                        preload="metadata"
                        playsInline
                        controlsList="nodownload"
                        onContextMenu={e => e.preventDefault()}
                        src={VideoBaseUrl + VideoFiles[videoVersion]}
                    >
                        Seu navegador não suporta a reprodução deste vídeo.
                    </Styled.VideoPlayer>

                    <Styled.VideoVersions>
                        <Styled.VideoVersionsLabel>VÍDEO DISPONÍVEL TAMBÉM EM:</Styled.VideoVersionsLabel>
                        <Styled.VideoVersionsButtons>
                            <Styled.VideoVersionButton
                                type="button"
                                $active={videoVersion === TipoAcessibilidade.LIBRAS}
                                aria-pressed={videoVersion === TipoAcessibilidade.LIBRAS}
                                aria-label="Assistir ao vídeo em Libras"
                                onClick={() => handleSelectVersion(TipoAcessibilidade.LIBRAS)}
                            >
                                <img src={iconLibras} alt="" />
                                <span>EM LIBRAS</span>
                            </Styled.VideoVersionButton>
                            <Styled.VideoVersionButton
                                type="button"
                                $active={videoVersion === TipoAcessibilidade.AUDIODESCRICAO}
                                aria-pressed={videoVersion === TipoAcessibilidade.AUDIODESCRICAO}
                                aria-label="Assistir ao vídeo com audiodescrição"
                                onClick={() => handleSelectVersion(TipoAcessibilidade.AUDIODESCRICAO)}
                            >
                                <img src={iconAudiodescricao} alt="" />
                                <span>AUDIODESCRIÇÃO</span>
                            </Styled.VideoVersionButton>
                        </Styled.VideoVersionsButtons>
                    </Styled.VideoVersions>
                </Styled.VideoModalBody>
            </Styled.VideoModal>
        </Styled.Page>
    );
};

export default SennaBeco;
